import LinkIcon from "@mui/icons-material/Link";
import StarRateIcon from "@mui/icons-material/StarRate";
import ThumbUpOffAltIcon from "@mui/icons-material/ThumbUpOffAlt";

import { REVIEW_STAR_GOLD, STAT_DISABLED_GREY } from "constants/index";
import { formatTrackingLinkSubmissionRate } from "utils";

import type { SvgIconComponent } from "@mui/icons-material";

import type { CarrierMetadata } from "types/company/carrier";

export interface CarrierStat {
  tooltip: string;
  description: string;
  icon: SvgIconComponent;
  iconColor: string;
  value: string;
  countLabel?: string;
  // The same figure as `countLabel`, unformatted, for the compact `4.7 (12)`
  // presentation that has no room for the word "reviews".
  count?: number;
}

interface BuildCarrierStatsProps {
  carrierMeta: CarrierMetadata;
  withTrackingLinkSubmissionRate: boolean;
}

/** Renders a review count as `(1 review)` / `(4 reviews)`. */
function formatReviewCountLabel(count: number): string {
  return count === 1 ? `(${count} review)` : `(${count} reviews)`;
}

/** Builds the stats a carrier row renders, in display order. */
export function buildCarrierStats(
  props: BuildCarrierStatsProps,
): CarrierStat[] {
  const { carrierMeta, withTrackingLinkSubmissionRate } = props;
  const { averageReviewRating, microReviewRating, trackingLinkSubmissionRate } =
    carrierMeta;

  const stats: CarrierStat[] = [
    {
      tooltip: "Reviews on the carrier company",
      description: "Average Rating:",
      icon: StarRateIcon,
      iconColor:
        averageReviewRating.count === 0 ? STAT_DISABLED_GREY : REVIEW_STAR_GOLD,
      value: String(averageReviewRating.average),
      countLabel: formatReviewCountLabel(averageReviewRating.count),
      count: averageReviewRating.count,
    },
    {
      tooltip: "Satisfaction rate on shipments booked by this carrier",
      description: "Satisfaction:",
      icon: ThumbUpOffAltIcon,
      iconColor: "primary.main",
      value: `${microReviewRating.rating}%`,
      countLabel: formatReviewCountLabel(microReviewRating.microReviewsCount),
      count: microReviewRating.microReviewsCount,
    },
  ];

  // `!= null` rather than `!== null`: a response from a backend older than the
  // field omits the key entirely, and `undefined` would otherwise reach
  // `Math.round` and render "NaN%".
  if (withTrackingLinkSubmissionRate && trackingLinkSubmissionRate != null) {
    stats.push({
      tooltip:
        "How often this carrier submits a tracking link after offering one on a quote",
      description: "Tracking Links Submitted:",
      icon: LinkIcon,
      iconColor: "primary.light",
      value: formatTrackingLinkSubmissionRate(trackingLinkSubmissionRate),
    });
  }

  return stats;
}
